/**
 * Utility functions for handling user permissions from JWT tokens
 */

/**
 * Decode JWT token (without verification - only for reading claims)
 * @param {string} token - JWT token
 * @returns {object} - Decoded token payload
 */
export function decodeJWT(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
}

/**
 * Get user permissions from localStorage token
 * @returns {object} - { hermes_permissions: [], olymp_permissions: [] }
 */
export function getUserPermissions() {
  const token = localStorage.getItem('authToken');
  if (!token) {
    return { hermes_permissions: [], olymp_permissions: [] };
  }

  const decoded = decodeJWT(token);
  if (!decoded) {
    return { hermes_permissions: [], olymp_permissions: [] };
  }

  return {
    hermes_permissions: decoded.hermes_permissions || [],
    olymp_permissions: decoded.olymp_permissions || [],
  };
}

/**
 * Check if user has a specific Hermes permission
 * @param {string} permission - Permission codename (e.g., 'view_dashboard')
 * @returns {boolean}
 */
export function hasHermesPermission(permission) {
  const { hermes_permissions } = getUserPermissions();
  return hermes_permissions.includes(permission);
}

/**
 * Check if user has any of the specified permissions
 * @param {array} permissions - Array of permission codenames
 * @returns {boolean}
 */
export function hasAnyHermesPermission(permissions) {
  const { hermes_permissions } = getUserPermissions();
  return permissions.some((perm) => hermes_permissions.includes(perm));
}

/**
 * Check if user has all of the specified permissions
 * @param {array} permissions - Array of permission codenames
 * @returns {boolean}
 */
export function hasAllHermesPermissions(permissions) {
  const { hermes_permissions } = getUserPermissions();
  return permissions.every((perm) => hermes_permissions.includes(perm));
}

/**
 * Filter menu items based on required permissions
 * @param {array} menuItems - Array of menu item objects
 * @returns {array} - Filtered menu items
 */
export function filterMenuItemsByPermissions(menuItems) {
  const { hermes_permissions } = getUserPermissions();

  return menuItems.filter((item) => {
    // If no permission required, show the item
    if (!item.requiredPermission) {
      return true;
    }

    // If permission required, check if user has it
    return hermes_permissions.includes(item.requiredPermission);
  });
}
